/*
 * ELCHIS theme – product page components
 * media-gallery, sticky-atc, recently viewed, product recommendations, bought together,
 * anchor navigation. Requires global.js (loaded in the layout before this file).
 */
(() => {
  if (!window.Elchis || window.ElchisProduct) return;
  window.ElchisProduct = true;

  const { define, parseHTML, formatMoney, toast, announce, openDialog, Cart, strings, routes, motionMs } = window.Elchis;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const RECENT_KEY = 'elchis:recently-viewed';

  const storage = {
    get(key, fallback) {
      try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
      } catch (error) {
        return fallback;
      }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (error) { /* storage unavailable */ }
    },
    remove(key) {
      try { window.localStorage.removeItem(key); } catch (error) { /* storage unavailable */ }
    }
  };

  const rootUrl = () => (routes.root || '/').replace(/\/$/, '');

  /* --------------------------------------------------------------- gallery */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.slides = [...this.querySelectorAll('.gallery__slide[data-media-id]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.dots = [...this.querySelectorAll('.gallery__dot')];
      this.counter = this.querySelector('[data-gallery-index]');
      this.zoomDialog = this.querySelector('.modal--zoom');
      if (!this.stage || this._bound) return;
      this._bound = true;

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-thumb]');
        if (thumb) {
          this.goTo(thumb.dataset.thumb);
          return;
        }
        const zoom = event.target.closest('[data-zoom]');
        if (zoom && this.zoomDialog) this.openZoom(zoom.dataset.zoom, zoom);
      });

      let frame;
      this.stage.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.syncFromScroll());
      }, { passive: true });

      this.root = this.closest('[data-product-root]');
      this.onVariant = (event) => {
        if (event.detail && event.detail.mediaId) this.goTo(event.detail.mediaId);
      };
      if (this.root) this.root.addEventListener('variant:change', this.onVariant);
    }

    disconnectedCallback() {
      if (this.root) this.root.removeEventListener('variant:change', this.onVariant);
    }

    goTo(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      this.stage.scrollTo({ left: slide.offsetLeft - this.stage.offsetLeft - (parseFloat(getComputedStyle(this.stage).paddingLeft) || 0), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      this.setActive(this.slides.indexOf(slide));
    }

    syncFromScroll() {
      const left = this.stage.scrollLeft;
      let best = 0;
      let distance = Infinity;
      const pad = parseFloat(getComputedStyle(this.stage).paddingLeft) || 0;
      this.slides.forEach((slide, index) => {
        const d = Math.abs(slide.offsetLeft - this.stage.offsetLeft - pad - left);
        if (d < distance) { distance = d; best = index; }
      });
      this.setActive(best);
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        if (!active) this.pauseMedia(slide);
      });
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = index + 1;
    }

    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((iframe) => {
        const target = iframe.src || '';
        if (target.includes('youtube')) iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        if (target.includes('vimeo')) iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      });
    }

    openZoom(mediaId, opener) {
      openDialog(this.zoomDialog, opener);
      const img = this.zoomDialog.querySelector(`#Zoom-${CSS.escape(this.dataset.section)}-${CSS.escape(mediaId)}`);
      if (img) {
        img.loading = 'eager';
        requestAnimationFrame(() => img.scrollIntoView({ block: 'start' }));
      }
    }
  }
  define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------ sticky ATC */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const root = this.closest('[data-product-root]');
      this.target = root && root.querySelector('[data-main-buy]');
      if (!this.target) return;
      this.hidden = false;
      this.setAttribute('inert', '');
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.toggle(past);
      }, { threshold: 0 });
      this.observer.observe(this.target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-bar');
    }

    toggle(show) {
      if (show === this.visible) return;
      this.visible = show;
      this.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-bar', show);
      if (show) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }
  }
  define('sticky-atc', StickyAtc);

  /* ------------------------------------------------------- recently viewed */

  class RecentlyViewedRecorder extends HTMLElement {
    connectedCallback() {
      const handle = this.dataset.handle;
      if (!handle || window.Elchis.config.designMode) return;
      const list = storage.get(RECENT_KEY, []).filter((h) => h !== handle);
      list.unshift(handle);
      storage.set(RECENT_KEY, list.slice(0, 12));
    }
  }
  define('recently-viewed-recorder', RecentlyViewedRecorder);

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-recent-clear]')) return;
        storage.remove(RECENT_KEY);
        this.hidden = true;
      });
      this.render();
    }

    async render() {
      const limit = parseInt(this.dataset.limit || '4', 10);
      const exclude = this.dataset.exclude;
      const handles = storage.get(RECENT_KEY, []).filter((h) => h && h !== exclude).slice(0, limit);
      const list = this.querySelector('[data-recent-list]');
      const template = this.querySelector('[data-recent-template]');
      if (!handles.length || !list || !template) return;

      const results = await Promise.all(handles.map(async (handle) => {
        try {
          const response = await fetch(`${rootUrl()}/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
          return response.ok ? response.json() : null;
        } catch (error) {
          return null;
        }
      }));

      const products = results.filter(Boolean);
      if (!products.length) return;
      list.innerHTML = '';
      products.forEach((product) => {
        const node = template.content.cloneNode(true);
        const link = node.querySelector('a');
        const img = node.querySelector('img');
        link.href = product.url || `${rootUrl()}/products/${product.handle}`;
        node.querySelector('.mini__title').textContent = product.title;
        const price = formatMoney(product.price);
        node.querySelector('.mini__price').textContent = product.price_varies ? `${strings.priceFrom} ${price}` : price;
        if (product.featured_image) {
          const src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
          img.src = `${src}${src.includes('?') ? '&' : '?'}width=144`;
          img.alt = product.title;
        } else {
          img.remove();
        }
        list.appendChild(node);
      });
      this.hidden = false;
    }
  }
  define('recently-viewed', RecentlyViewed);

  /* ------------------------------------------------ product recommendations */

  const lazyLoad = (element, callback) => {
    if (!('IntersectionObserver' in window)) {
      callback();
      return null;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        callback();
      }
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(element);
    return observer;
  };

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._loaded || !this.dataset.url || this.querySelector('.product-grid, .recs__grid')) return;
      this._loaded = true;
      this.observer = lazyLoad(this, () => this.load());
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const incoming = parseHTML(await response.text()).querySelector('product-recommendations');
        if (incoming && incoming.innerHTML.trim()) this.innerHTML = incoming.innerHTML;
        else this.remove();
      } catch (error) {
        this.remove();
      }
    }
  }
  define('product-recommendations', ProductRecommendations);

  /* -------------------------------------------------------- bought together */

  class FbtBundle extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[data-fbt-item]')) {
        this.init();
      } else if (this.dataset.url && !this._loaded) {
        this._loaded = true;
        this.observer = lazyLoad(this, () => this.load());
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const incoming = parseHTML(await response.text()).querySelector('fbt-bundle');
        if (incoming && incoming.querySelector('[data-fbt-item]')) {
          this.innerHTML = incoming.innerHTML;
          this.init();
        } else {
          this.remove();
        }
      } catch (error) {
        this.remove();
      }
    }

    init() {
      if (this.querySelectorAll('[data-fbt-item]').length < 2) {
        this.remove();
        return;
      }
      if (!this._bound) {
        this._bound = true;
        this.addEventListener('change', (event) => {
          const toggle = event.target.closest('[data-fbt-toggle]');
          if (!toggle) return;
          const item = toggle.closest('[data-fbt-item]');
          item.classList.toggle('is-on', toggle.checked);
          toggle.closest('.cbx').classList.toggle('is-on', toggle.checked);
          this.update();
        });
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-fbt-add]')) this.add(event.target.closest('[data-fbt-add]'));
        });
      }
      this.update();
    }

    selected() {
      return [...this.querySelectorAll('[data-fbt-item]')].filter((item) => {
        const toggle = item.querySelector('[data-fbt-toggle]');
        return toggle && toggle.checked;
      });
    }

    update() {
      const items = this.selected();
      const total = items.reduce((sum, item) => sum + parseInt(item.dataset.price || '0', 10), 0);
      const totalEl = this.querySelector('[data-fbt-total]');
      if (totalEl) totalEl.textContent = formatMoney(total);
      const button = this.querySelector('[data-fbt-add]');
      if (button) button.disabled = items.length === 0;
    }

    async add(button) {
      const items = this.selected().map((item) => ({ id: parseInt(item.dataset.variantId, 10), quantity: 1 }));
      if (!items.length) return;
      const error = this.querySelector('[data-fbt-error]');
      const label = button.querySelector('[data-btn-label]');
      const original = label ? label.textContent : '';
      if (error) error.hidden = true;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (label) label.textContent = strings.adding;
      try {
        await Cart.add(items);
        if (label) label.textContent = strings.added;
        announce(strings.addedToCart);
        const drawer = Cart.drawer();
        if (window.Elchis.config.cartType === 'page') window.location.href = routes.cart_url;
        else if (drawer) drawer.open(button);
        else toast({ message: strings.addedToCart });
      } catch (err) {
        if (error) {
          error.textContent = err.message || strings.genericError;
          error.hidden = false;
        } else {
          toast({ message: err.message || strings.genericError, type: 'error' });
        }
      } finally {
        setTimeout(() => {
          button.removeAttribute('aria-busy');
          button.disabled = false;
          if (label) label.textContent = original;
        }, motionMs(1200) || 600);
      }
    }
  }
  define('fbt-bundle', FbtBundle);

  /* --------------------------------------------------------- anchor nav */

  const initAnchorNav = () => {
    document.querySelectorAll('.subnav:not(.subnav--collection):not(.subnav--flush)').forEach((nav) => {
      if (nav._spy) return;
      const allLinks = [...nav.querySelectorAll('a[href^="#"]')];
      // Hide links to sections that are not rendered for this product (empty metafields).
      allLinks.forEach((a) => {
        const target = document.getElementById(decodeURIComponent(a.hash.slice(1)));
        a.hidden = !target || target.offsetHeight === 0;
      });
      const links = allLinks.filter((a) => !a.hidden);
      if (links.length < 2) {
        nav.hidden = true;
        return;
      }
      const targets = links.map((a) => document.getElementById(decodeURIComponent(a.hash.slice(1)))).filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;
      nav._spy = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((a) => {
            const active = a.hash.slice(1) === entry.target.id;
            a.classList.toggle('is-active', active);
            if (active) a.setAttribute('aria-current', 'true');
            else a.removeAttribute('aria-current');
          });
        });
      }, { rootMargin: '-40% 0px -55% 0px' });
      targets.forEach((t) => nav._spy.observe(t));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAnchorNav);
  else initAnchorNav();
  document.addEventListener('shopify:section:load', initAnchorNav);
})();
